// Red del modo "Replicar una foto" — los dos pedidos que no son `generateFreeformImage`
// (ADR-2026-09-13-replicar-una-foto-tres-pipelines). Separado del dominio
// (`replicarFoto.ts`, puro) y de la pantalla, como `plantillasPropiasApi`.

import { USE_ATLAS_BACKEND } from '../../config';
import { atlasBackendFetch, pollJobUntilSettled } from '../../lib/atlasBackend';
import { postImageEdit } from '../../lib/imageEdit';
import { GenerateAdError } from '../../lib/generateAdRequest';
import type { RubroPrompt } from './replicarFoto';

/**
 * NIVEL BAJO, paso 1: la visión describe la foto como un prompt.
 *
 * `/v1/media/analyze` es GRATIS y síncrono para una imagen (Gemini con visión); si el
 * backend responde 202 (cola lenta) se polea el job. `url` tiene que ser una URL
 * FIRMADA de nuestro storage: la ruta rechaza cualquier otra (SSRF).
 */
export async function describirPlantilla(opts: { url: string; prompt: string }): Promise<string> {
  return analizarImagenes({ urls: [opts.url], prompt: opts.prompt });
}

/**
 * Lo mismo con VARIAS imágenes (hasta 5, el tope de la ruta), en el orden dado: la
 * mezcla del modo Diseño le muestra la base y las partes a la visión para que escriba
 * el prompt (ADR-2026-09-18-mezclar-escribe-su-prompt).
 */
export async function analizarImagenes(opts: { urls: string[]; prompt: string }): Promise<string> {
  const res = await atlasBackendFetch('/v1/media/analyze', {
    urls: opts.urls,
    prompt: opts.prompt,
  });
  const leer = (out: unknown): string =>
    out && typeof out === 'object' && typeof (out as { response?: unknown }).response === 'string'
      ? ((out as { response: string }).response ?? '').trim()
      : '';
  if (res.status === 202) {
    const pending = (await res.json().catch(() => ({}))) as { job_id?: string };
    if (!pending.job_id) throw new Error('No se pudo describir la foto. Intentá de nuevo.');
    const settled = await pollJobUntilSettled(pending.job_id, { timeoutMs: 3 * 60 * 1000 });
    if (!settled || settled.status !== 'succeeded') {
      throw new Error('No se pudo describir la foto. Intentá de nuevo.');
    }
    const texto = leer(settled.output);
    if (!texto) throw new Error('La descripción de la foto llegó vacía. Probá de nuevo.');
    return texto;
  }
  const data = (await res.json().catch(() => null)) as { response?: string; error?: string } | null;
  if (!res.ok) throw new Error(data?.error || 'No se pudo describir la foto. Intentá de nuevo.');
  const texto = leer(data);
  if (!texto) throw new Error('La descripción de la foto llegó vacía. Probá de nuevo.');
  return texto;
}

/**
 * NIVEL ALTO: el backend EDITA la foto en un solo pedido (modo `replicar` de
 * `/v1/images/edit`) y devuelve el id de la fila nueva.
 *
 * Sólo contra el backend nuevo: el modo no existe en el webhook viejo de n8n, y el
 * fallback devolvería una edición cualquiera (cobrada).
 */
export async function editarReplicando(opts: {
  companyId: string;
  userId: string;
  /** La plantilla propia: bucket `modelos`, path `<company>/plantillas_propias/…`. */
  plantilla: { bucket: string; path: string };
  /** Los productos elegidos, si hay (hasta 4). Van en orden: el primero es el principal. */
  productoIds?: string[] | null;
  /** La foto de la modelo (URL firmada) o su descripción en palabras, si hay. */
  modeloUrl?: string | null;
  descripcionModelo?: string | null;
  instruccion: string;
  /** El formato pedido, como ratio ("9:16"). */
  aspectRatio: string;
  /** Prenda puesta (moda) u objeto usado (el resto) — decide las reglas del backend. */
  rubro: RubroPrompt;
}): Promise<string> {
  if (!USE_ATLAS_BACKEND) {
    throw new Error('Este nivel de fidelidad no está disponible en este entorno.');
  }
  const data = await postImageEdit({
    user_id: opts.userId,
    mode: 'replicar',
    image_bucket: opts.plantilla.bucket,
    image_path: opts.plantilla.path,
    // `variante_id` va SIEMPRE con el primero, además de la lista: un backend que todavía
    // no conozca `variante_ids` edita igual con el principal.
    ...(opts.productoIds && opts.productoIds.length > 0
      ? { variante_id: opts.productoIds[0], variante_ids: opts.productoIds }
      : {}),
    ...(opts.modeloUrl ? { modelo_url: opts.modeloUrl } : {}),
    ...(!opts.modeloUrl && opts.descripcionModelo ? { descripcion_modelo: opts.descripcionModelo } : {}),
    ...(opts.instruccion.trim() ? { instruction: opts.instruccion.trim() } : {}),
    aspect_ratio: opts.aspectRatio,
    rubro: opts.rubro,
    modo_utilizado: 'replicar_foto_editar',
  });
  if (data.response === 'error_moderation') {
    // `policy_violation` es accionable: la pantalla muestra este mensaje en vez del
    // genérico, porque reintentar igual no va a funcionar.
    throw new GenerateAdError(
      'policy_violation',
      'La imagen no pasó el filtro de contenido. Probá con otra foto o con otras indicaciones.',
      'error_moderation',
    );
  }
  const id = data.edited_image_id ?? data.imagen_id ?? data.id;
  if (data.response !== 'success' || !id) {
    throw new Error(data.error || 'No se pudo editar la foto. Intentá de nuevo.');
  }
  return String(id);
}
